import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {RREmployeesType, RREmployeeType,} from "../app/types/RREmployeeType";
import {RootState} from "../app/store";
import {AppFirebaseState} from "../app/types/AppFirebaseState";
import {MaybeString} from "../app/types/Maybe";
import {AppFirebaseLoadingErrorState} from "../app/types/AppFirebaseLoadingErrorState";
import {RREventsType, RREventType} from "../app/types/RREventType";
import {RRCalendarDaysType} from "../app/types/RRCalendarDayType";
import {RRBankHolidaysType} from "../app/types/RRBankHolidayType";
import {dateWithZeroTime} from "../app/tools";
import {RREmployeeStatisticType} from "../app/types/RREmployeeStatisticType";
import {Root} from "react-dom/client";
import {WarehouseLocksState} from "../app/warehouse/warehouseLocks";

type AppDataEmployeesType = {
    loading: boolean;
    error?: MaybeString;
    current: RREmployeesType; // not deleted and not leaved
    all: RREmployeesType  // all employees
}

export type AppDataType = {
    employees: AppDataEmployeesType;
    employeesWorkhours: Map<string, RREmployeeStatisticType>;
    events: AppFirebaseState<RREventsType>;
    allEvents: AppFirebaseState<RREventsType>;
    calendar: AppFirebaseState<RRCalendarDaysType>;
    bankHolidays: AppFirebaseState<RRBankHolidaysType>;
    warehouseLocks: WarehouseLocksState;
}


const initialState: AppDataType = {
    employees: {
        all: [], current: [], loading: true,
    },
    employeesWorkhours: new Map<string, RREmployeeStatisticType>(),
    events: {data: [], loading: true},
    allEvents: {data: [], loading: false},
    calendar: {data: [], loading: true},
    bankHolidays: {data: [], loading: false},
    warehouseLocks: {data: [], loading: true},
}

export const appDataSlice = createSlice({
        name: 'data',
        initialState,
        reducers: {
            setEmployees: (state, action: PayloadAction<{ data: AppFirebaseState<RREmployeesType>, firstDay: Date, lastDay: Date }>) => {
                state.employees.all = action.payload.data.data || [];
                const firstDay = dateWithZeroTime(action.payload.firstDay)!;
                const lastDay = dateWithZeroTime(action.payload.lastDay)!;
                state.employees.current = state.employees.all?.filter(
                    value => {
                        if (value.isDeleted) return false;
                        if (value.startDate && value.startDate > lastDay) return false;
                        if (value.leaveDate && value.leaveDate < firstDay) return false;
                        return true;
                    }
                ) || [];

                state.employees.loading = action.payload.data.loading;
                state.employees.error = action.payload.data.error;
            },

            setEmployeesWorkhours: (state, action: PayloadAction<Map<string, RREmployeeStatisticType>>) => {
                state.employeesWorkhours = action.payload
            },

            setEvents: (state, action: PayloadAction<AppFirebaseState<RREventsType>>) => {
                state.events = action.payload;
            },

            setAllEvents: (state, action: PayloadAction<AppFirebaseState<RREventsType>>) => {
                state.allEvents = action.payload;
            },

            setCalendar: (state, action: PayloadAction<AppFirebaseState<RRCalendarDaysType>>) => {
                state.calendar = action.payload;
            },

            setBankHolidays: (state, action: PayloadAction<AppFirebaseState<RRBankHolidaysType>>) => {
                state.bankHolidays = action.payload;
            },

            setWarehouseLocks: (state, action: PayloadAction<WarehouseLocksState>) => {
                state.warehouseLocks = action.payload;
            },
        },
    }
)

export const {setEmployees,
    setEmployeesWorkhours,
    setEvents,
    setAllEvents,
    setCalendar,
    setBankHolidays,
    setWarehouseLocks,
} = appDataSlice.actions

// Other code such as selectors can use the imported `RootState` type
export const selectCurrentEmployees = (state: RootState) => state.data.employees.current;
export const selectAllEmployees = (state: RootState) => state.data.employees.all;
export const selectEmployeesStatus = (state: RootState) => {
    return {loading: state.data.employees.loading, error: state.data.employees.error} as AppFirebaseLoadingErrorState
};

export const selectEvents = (state: RootState) => state.data.events;

export const selectAllEvents = (state: RootState) => state.data.allEvents;

export const selectCalendar = (state: RootState) => state.data.calendar;
export const selectBankHolidays = (state: RootState) => state.data.bankHolidays;
export const selectEmployeesWorkhours = (state: RootState) => state.data.employeesWorkhours;
export const selectWarehouseLocks = (state: RootState) => state.data.warehouseLocks;

export const AppDataReducer = appDataSlice.reducer;
